import React, { useEffect, useMemo, useState } from "react";
import Modal from "antd/es/modal/Modal";
import { toast } from "react-toastify";

const ALLOWED_MODULES = [
  "dashboard",
  "colaboradores",
  "representantes",
  "deportistas",
  "grupos",
  "eventos",
  "asistencias",
  "notificaciones",
  "roles",
  "pagos",
  "productos",
];

/** Construye un objeto base con read en false (sin write) */
function buildEmptyPerms() {
  return ALLOWED_MODULES.reduce((acc, m) => {
    acc[m] = { read: false };
    return acc;
  }, /** @type {Record<string, {read: boolean}>} */ ({}));
}

/** Limpia permisos dejando sólo los módulos válidos y solo read */
function sanitizePerms(incoming) {
  const base = buildEmptyPerms();
  ALLOWED_MODULES.forEach((m) => {
    base[m] = {
      // Permitimos true, {read:true} o truthy equivalentes
      read: Boolean(incoming?.[m]?.read || incoming?.[m] === true),
    };
  });
  return base;
}

/**
 * @param {{
 *  open: boolean;
 *  editing?: { id?: string; nombre?: string; descripcion?: string; activo?: boolean; permisos?: any } | null;
 *  saving?: boolean;
 *  onClose: () => void;
 *  onSubmit: (payload: { nombre: string; descripcion: string; permisos: Record<string, {read: boolean}>; activo: boolean }) => void;
 * }} props
 */
export function ModalRol({ open, editing, saving = false, onClose, onSubmit }) {
  const isEditing = !!editing;
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [activo, setActivo] = useState(true);
  const [permisos, setPermisos] = useState(buildEmptyPerms());

  // Cargar/limpiar estado al abrir o cambiar "editing"
  useEffect(() => {
    if (editing) {
      setNombre(editing.nombre || "");
      setDescripcion(editing.descripcion || "");
      setActivo(editing.activo ?? true);
      setPermisos(sanitizePerms(editing.permisos || {}));
    } else {
      // Al crear nuevo, siempre activo
      setNombre("");
      setDescripcion("");
      setActivo(true);
      setPermisos(buildEmptyPerms());
    }
  }, [editing, open]);

  const modulesList = useMemo(() => ALLOWED_MODULES, []);

  function toggleRead(mod) {
    setPermisos((s) => ({
      ...s,
      [mod]: { read: !Boolean(s[mod]?.read) },
    }));
  }

  function selectAll() {
    setPermisos((s) => {
      const copy = { ...s };
      modulesList.forEach((m) => (copy[m] = { read: true }));
      return copy;
    });
  }

  function clearAll() {
    setPermisos((s) => {
      const copy = { ...s };
      modulesList.forEach((m) => (copy[m] = { read: false }));
      return copy;
    });
  }

  function submit() {
    if (!nombre.trim()) {
      toast.error("El nombre del rol es obligatorio");
      return;
    }
    // Reglas: Al crear, SIEMPRE activo (aunque el switch esté en falso)
    const finalActivo = isEditing ? activo : true;

    const payload = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim(),
      permisos: sanitizePerms(permisos),
      activo: finalActivo,
    };
    onSubmit(payload);
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">
            {isEditing ? "Editar rol" : "Nuevo rol"}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 transition disabled:opacity-60"
            disabled={saving}
          >
            {saving ? "Guardando..." : isEditing ? "Guardar" : "Crear"}
          </button>
        </div>
      }
      centered
      width={720}
      destroyOnClose
      maskClosable={false}
      keyboard
      // Altura máxima del modal y scroll SOLO del cuerpo
      styles={{
        content: { borderRadius: 16 },
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8, // pequeño espacio para que no tape la barra de scroll
        },
      }}
    >
      <div className="space-y-5">
        {/* Datos base */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nombre */}
          <div>
            <label
              htmlFor="rol-nombre"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              id="rol-nombre"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              placeholder="ej: entrenador, representante"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </div>

          {/* Estado */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Estado
            </label>
            <label
              className={
                "inline-flex items-center gap-2.5 px-3 py-2 rounded-lg border " +
                (isEditing
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-70") +
                " border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              }
              title={
                isEditing
                  ? "Puedes activar o inactivar el rol"
                  : "Al crear, el rol se establece activo automáticamente"
              }
            >
              <input
                type="checkbox"
                className="peer sr-only"
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                disabled={!isEditing || saving}
              />
              <span
                className={
                  "w-10 h-6 rounded-full transition-all relative " +
                  (activo ? "bg-emerald-500" : "bg-slate-300")
                }
              >
                <span
                  className={
                    "absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform " +
                    (activo ? "translate-x-4" : "translate-x-0")
                  }
                />
              </span>
              <span className="font-medium">
                {activo ? "Activo" : "Inactivo"}
              </span>
            </label>
          </div>

          {/* Descripción */}
          <div className="md:col-span-2">
            <label
              htmlFor="rol-descripcion"
              className="block text-xs font-medium text-slate-600 mb-1"
            >
              Descripción
            </label>
            <textarea
              id="rol-descripcion"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100"
              rows={2}
              placeholder="Descripción del rol"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              disabled={saving}
            />
          </div>
        </section>

        {/* Permisos (solo read) */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-slate-900">
              Permisos de lectura por módulo
            </h4>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100"
                disabled={saving}
              >
                Seleccionar todo
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-100"
                disabled={saving}
              >
                Limpiar
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Marca los módulos a los que el rol tendrá acceso de lectura.
          </p>

          {/* Contenedor con scroll propio, además del body */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1">
            {modulesList.map((mod) => {
              const checked = !!permisos?.[mod]?.read;
              return (
                <label
                  key={mod}
                  className={
                    "flex items-center justify-between rounded-xl border px-3 py-3 cursor-pointer transition-all " +
                    (checked
                      ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"
                      : "border-slate-200 bg-white hover:bg-slate-50")
                  }
                >
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                      checked={checked}
                      onChange={() => toggleRead(mod)}
                      disabled={saving}
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900 capitalize">
                        {mod}
                      </div>
                      <div className="text-xs text-slate-500">
                        {checked ? "✓ Puede leer" : "✗ Sin acceso"}
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>
      </div>
    </Modal>
  );
}
